import React, { useState } from 'react';
import { format } from 'date-fns';

const LoanApprovalCard = ({ 
  loan, 
  onApprove, 
  onDisapprove, 
  onDisburse, 
  isProcessing = false,
  selected = false,
  onSelect = null 
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [disapprovalReason, setDisapprovalReason] = useState('');
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showDisapprovalModal, setShowDisapprovalModal] = useState(false);
  const [showDisbursementModal, setShowDisbursementModal] = useState(false);
  const [disbursementNotes, setDisbursementNotes] = useState('');

  const handleApprove = () => {
    if (approvalNotes.trim()) {
      onApprove(loan.id, approvalNotes);
      setShowApprovalModal(false);
      setApprovalNotes('');
    }
  };

  const handleDisapprove = () => {
    if (disapprovalReason.trim()) {
      onDisapprove(loan.id, disapprovalReason);
      setShowDisapprovalModal(false);
      setDisapprovalReason('');
    }
  };

  const handleDisburse = () => {
    onDisburse(loan.id, disbursementNotes);
    setShowDisbursementModal(false);
    setDisbursementNotes('');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'PENDING': { color: 'bg-yellow-100 text-yellow-800', text: 'Pending' },
      'APPROVED': { color: 'bg-green-100 text-green-800', text: 'Approved' },
      'DISAPPROVED': { color: 'bg-red-100 text-red-800', text: 'Disapproved' },
      'ACTIVE': { color: 'bg-blue-100 text-blue-800', text: 'Active' }
    };

    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', text: status };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(amount);
  };

  return (
    <>
      <div className={`bg-white rounded-lg shadow-md border ${selected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'} p-6 mb-4`}>
        {/* Header with checkbox and status */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            {onSelect && (
              <input
                type="checkbox"
                checked={selected}
                onChange={(e) => onSelect(loan.id, e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            )}
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Loan #{loan.loan_no}
              </h3>
              <p className="text-sm text-gray-500">
                Applied on {format(new Date(loan.created_at), 'MMM dd, yyyy')}
              </p>
            </div>
          </div>
          {getStatusBadge(loan.status)}
        </div>

        {/* Member Information */}
        <div className="mb-4">
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <h4 className="font-medium text-gray-900">{loan.member?.name}</h4>
              <p className="text-sm text-gray-500">{loan.member?.phone_number}</p>
              {loan.applied_by && (
                <p className="text-xs text-gray-400">
                  Applied by: {loan.applied_by.name}
                </p>
              )}
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-gray-900">
                {formatCurrency(loan.principal)}
              </p>
              <p className="text-sm text-gray-500">
                {loan.formatted_duration || `${loan.duration} months`}
              </p>
            </div>
          </div>
        </div>

        {/* Loan Details */}
        <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
          <div>
            <p className="text-gray-500">Loan Type</p>
            <p className="font-medium">{loan.loan_type?.name}</p>
          </div>
          <div>
            <p className="text-gray-500">{loan.installment_period_label || 'Monthly Installment'}</p>
            <p className="font-medium">{formatCurrency(loan.monthly_installment)}</p>
          </div>
          <div>
            <p className="text-gray-500">Total Amount</p>
            <p className="font-medium">{formatCurrency(loan.total_amount)}</p>
          </div>
          <div>
            <p className="text-gray-500">Interest Rate</p>
            <p className="font-medium">{loan.rate}%</p>
          </div>
        </div>

        {/* Reason */}
        {loan.reason && (
          <div className="mb-4">
            <p className="text-gray-500 text-sm">Reason</p>
            <p className="text-sm">{loan.reason}</p>
          </div>
        )}

        {/* Document Status */}
        {loan.requires_documents && (
          <div className="mb-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Documents:</span>
              {loan.has_all_required_documents ? (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Complete
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  Incomplete
                </span>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            {showDetails ? 'Hide Details' : 'View Details'}
          </button>

          <div className="flex space-x-2">
            {loan.can_be_approved && (
              <button
                onClick={() => setShowApprovalModal(true)}
                disabled={isProcessing}
                className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? 'Processing...' : 'Approve'}
              </button>
            )}
            
            {loan.can_be_disapproved && (
              <button
                onClick={() => setShowDisapprovalModal(true)}
                disabled={isProcessing}
                className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? 'Processing...' : 'Disapprove'}
              </button>
            )}

            {loan.can_be_disbursed && (
              <button
                onClick={() => setShowDisbursementModal(true)}
                disabled={isProcessing}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? 'Processing...' : 'Disburse'}
              </button>
            )}
          </div>
        </div>

        {/* Expanded Details */}
        {showDetails && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Start Date</p>
                <p className="font-medium">{format(new Date(loan.start_date), 'MMM dd, yyyy')}</p>
              </div>
              <div>
                <p className="text-gray-500">End Date</p>
                <p className="font-medium">{format(new Date(loan.end_date), 'MMM dd, yyyy')}</p>
              </div>
              <div>
                <p className="text-gray-500">Interest Amount</p>
                <p className="font-medium">{formatCurrency(loan.interest)}</p>
              </div>
              <div>
                <p className="text-gray-500">Current Balance</p>
                <p className="font-medium">{formatCurrency(loan.loan_balance)}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Approval Modal */}
      {showApprovalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Approve Loan</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Approval Notes (Optional)
              </label>
              <textarea
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
                placeholder="Add any notes about this approval..."
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowApprovalModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                disabled={isProcessing}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                {isProcessing ? 'Processing...' : 'Approve'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Disapproval Modal */}
      {showDisapprovalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Disapprove Loan</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Disapproval *
              </label>
              <textarea
                value={disapprovalReason}
                onChange={(e) => setDisapprovalReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                rows="3"
                placeholder="Please provide a reason for disapproval..."
                required
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDisapprovalModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleDisapprove}
                disabled={isProcessing || !disapprovalReason.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {isProcessing ? 'Processing...' : 'Disapprove'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Disbursement Modal */}
      {showDisbursementModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Disburse Loan</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Disbursement Notes (Optional)
              </label>
              <textarea
                value={disbursementNotes}
                onChange={(e) => setDisbursementNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
                placeholder="Add any notes about this disbursement..."
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDisbursementModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleDisburse}
                disabled={isProcessing}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {isProcessing ? 'Processing...' : 'Disburse'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LoanApprovalCard; 